import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { User, Search, ChevronDown } from 'lucide-react';
import { getContactsRequest } from '../../store/contacts/contacts.action';
import {
  selectContacts,
  selectContactsLoading,
} from '../../store/contacts/contacts.selector';
import { IContact } from '../../types/contacts/contacts.types';

export interface ContactSelectorProps {
  value: string;
  onChange: (contact: IContact) => void;
  label?: string;
  required?: boolean;
  fetchWhenVisible?: boolean;
  isVisible?: boolean;
  autoSelectFirst?: boolean;
  name?: boolean;
  email?: boolean;
  className?: string;
}

const contactName = (c: IContact) =>
  (c.firstName || c.lastName
    ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()
    : c.email) ?? '';

function contactDisplay(
  c: IContact,
  showName: boolean,
  showEmail: boolean
): string {
  if (showEmail && !showName) return c.email ?? '';
  if (showEmail && showName) return `${contactName(c)} - ${c.email ?? ''}`;
  return contactName(c);
}

export default function ContactSelector({
  value,
  onChange,
  label = 'Select contact',
  required = true,
  fetchWhenVisible = true,
  isVisible = true,
  autoSelectFirst = false,
  name: showName = true,
  email: showEmail = false,
  className = '',
}: ContactSelectorProps) {
  const dispatch = useDispatch();
  const list = useSelector(selectContacts);
  const loading = useSelector(selectContactsLoading);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetch = !fetchWhenVisible || isVisible;

  useEffect(() => {
    if (open && triggerRef.current)
      setRect(triggerRef.current.getBoundingClientRect());
    else setRect(null);
  }, [open]);

  useEffect(() => {
    if (!fetch) return;
    const t = setTimeout(
      () =>
        dispatch(
          getContactsRequest({ page: 0, limit: 10, ...(q && { search: q }) })
        ),
      300
    );
    return () => clearTimeout(t);
  }, [fetch, dispatch, q]);

  useEffect(() => {
    if (!open) return;
    const onOut = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!triggerRef.current?.contains(n) && !panelRef.current?.contains(n)) {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onOut);
    return () => document.removeEventListener('mousedown', onOut);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (
      fetch &&
      !loading &&
      list.length > 0 &&
      !value &&
      autoSelectFirst &&
      list[0]?._id
    )
      onChange(list[0]);
  }, [fetch, loading, list, value, autoSelectFirst, onChange]);

  const term = q.toLowerCase();
  const filtered = term
    ? list.filter(
        (c) =>
          contactName(c).toLowerCase().includes(term) ||
          (c.email?.toLowerCase().includes(term) ?? false)
      )
    : list;

  const selected = selectedContact || list.find((c) => c._id === value);
  const display = value
    ? selected
      ? contactDisplay(selected, showName, showEmail)
      : 'Select a contact'
    : 'Select a contact';

  const btn =
    'w-full pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-background';
  const inputCls =
    'w-full pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-background';

  return (
    <div className={className}>
      <label className="block text-xs font-medium text-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <div className="relative" ref={triggerRef}>
        <button
          type="button"
          onClick={() => !loading && setOpen((o) => !o)}
          disabled={loading}
          className={`flex items-center justify-between ${btn} transition-colors hover:border-input disabled:bg-muted disabled:cursor-not-allowed text-left`}
        >
          <div className="flex items-center flex-1 min-w-0">
            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <span
              className={`truncate ${!value ? 'text-muted-foreground' : 'text-foreground'}`}
            >
              {display}
            </span>
          </div>
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {open &&
          rect &&
          createPortal(
            <div
              ref={panelRef}
              className="fixed z-[9999] bg-card border border-border rounded-lg shadow-lg overflow-hidden"
              style={{
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                minWidth: rect.width,
              }}
            >
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search contacts…"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto scrollbar-thin">
                {loading ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                    Loading contacts…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                    {q ? 'No contacts found' : 'No contacts available'}
                  </div>
                ) : (
                  filtered.map((c) => (
                    <button
                      key={c._id}
                      type="button"
                      onClick={() => {
                        setSelectedContact(c);
                        onChange(c);
                        setOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors ${
                        value === c._id
                          ? 'bg-primary/10 text-foreground font-medium'
                          : 'text-foreground'
                      }`}
                    >
                      {contactDisplay(c, showName, showEmail)}
                    </button>
                  ))
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
      {loading && !open && (
        <p className="text-muted-foreground text-xs mt-0.5">
          Loading contacts…
        </p>
      )}
      {!loading && list.length === 0 && !open && (
        <p className="text-muted-foreground text-xs mt-0.5">
          No contacts available
        </p>
      )}
    </div>
  );
}
